import { rem, useMantineColorScheme } from "@mantine/core";
import { InputErrorMessage } from "../InputErrorMessage";
import React, { ReactNode } from "react";
import classes from "./useFormInputStyles.module.css";

const ERROR_COLOR = "var(--mantine-color-V500-0)";
const FOCUSABLE_BORDER_COLOR =
  "var(--sa-form-input-bd-active, var(--sa-form-input-bd))";

type StylesOverride = Partial<Record<string, React.CSSProperties>>;

type ClassNamesRecord = Partial<Record<string, string>>;
type ClassNamesInput =
  | ClassNamesRecord
  | ((...args: never[]) => ClassNamesRecord)
  | undefined;

const withFocusClass = (record: ClassNamesRecord = {}): ClassNamesRecord => ({
  ...record,
  input: [classes.input, record.input].filter(Boolean).join(" "),
});

/**
 * Junta a classe de foco do design system às `classNames` do consumidor
 * (objeto ou função do Mantine), sem perder a classe `input` dele.
 */
export function mergeFormInputClassNames<T>(classNames: T): T {
  const value = classNames as ClassNamesInput;
  if (typeof value === "function") {
    return ((...args: never[]) => withFocusClass(value(...args))) as T;
  }
  return withFocusClass(value) as T;
}

interface UseFormInputStylesParams {
  error?: ReactNode;
  forceErrorState?: boolean;
  disabled?: boolean;
  overrideStyles?: StylesOverride;
}

export function useFormInputStyles({
  error,
  forceErrorState = false,
  disabled = false,
  overrideStyles,
}: UseFormInputStylesParams) {
  const { colorScheme } = useMantineColorScheme();

  const hasExplicitErrorMessage =
    error !== null && error !== undefined && typeof error !== "boolean";

  const hasError = forceErrorState || Boolean(error);

  const forwardedError = hasExplicitErrorMessage ? (
    <InputErrorMessage message={error} />
  ) : hasError ? (
    " "
  ) : undefined;

  const shouldShowErrorMessage = Boolean(hasExplicitErrorMessage);

  const override =
    overrideStyles && typeof overrideStyles === "object" ? overrideStyles : {};

  const baseStyles = {
    input: {
      backgroundColor: disabled
        ? "var(--mantine-color-disabled_input_background-0)"
        : colorScheme === "light"
          ? "var(--mantine-color-white)"
          : "var(--mantine-color-D500-0)",
      color: disabled
        ? "var(--mantine-color-disabled_input_text-0)"
        : undefined,
      // A cor da borda vem de variáveis: em foco, a classe do CSS Module troca
      // `--sa-form-input-bd-active` (inline venceria qualquer seletor de classe).
      "--sa-form-input-bd": hasError
        ? ERROR_COLOR
        : colorScheme === "light"
          ? "var(--mantine-color-L500-0)"
          : "var(--mantine-color-D500-0)",
      "--sa-form-input-bd-focus": hasError
        ? ERROR_COLOR
        : colorScheme === "light"
          ? "var(--mantine-color-L600-0)"
          : "var(--mantine-color-D300-0)",
      border: `${hasError ? "2px" : "1px"} solid ${FOCUSABLE_BORDER_COLOR}`,
      boxShadow: "none",
      paddingInlineEnd: rem(12),
      transition: "border-color 150ms ease, background-color 150ms ease",
    } as React.CSSProperties,
    label: {
      fontWeight: "bold" as const,
      color: hasError ? ERROR_COLOR : undefined,
      paddingBottom: rem(4),
    },
    error: {
      display: "block",
      color: ERROR_COLOR,
      visibility: shouldShowErrorMessage
        ? ("visible" as const)
        : ("hidden" as const),
    },
  };

  // Deep-merge each known part so consumers can override individual properties
  // without losing the base styles (e.g. overriding label.width keeps fontWeight).
  const resolvedStyles: StylesOverride = {
    input: {
      ...baseStyles.input,
      ...(override.input ?? {}),
      ...(hasError
        ? {
            border: `2px solid ${ERROR_COLOR}`,
          }
        : {}),
    },
    label: { ...baseStyles.label, ...(override.label ?? {}) },
    error: { ...baseStyles.error, ...(override.error ?? {}) },
    // Pass through any extra parts (e.g. dropdown, option, section)
    ...Object.fromEntries(
      Object.entries(override).filter(
        ([k]) => !["input", "label", "error"].includes(k)
      )
    ),
  };

  return {
    forwardedError,
    resolvedStyles,
  };
}
